import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Shield, AlertTriangle, CheckCircle, XCircle, Zap } from 'lucide-react'
import { riskApi } from '../api/client'
import { useWebSocketEvent } from '../hooks/useWebSocket'
import type { WebSocketHookReturn } from '../hooks/useWebSocket'
import './RiskMonitor.css'

interface GaugeProps {
  label: string
  value: number
  max: number
  warningThreshold?: number
  dangerThreshold?: number
  format?: (v: number) => string
}

function Gauge({
  label,
  value,
  max,
  warningThreshold = 60,
  dangerThreshold = 80,
  format,
}: GaugeProps) {
  const percentage = (value / max) * 100
  const displayValue = format ? format(value) : `${value.toFixed(1)}%`
  const displayMax = format ? format(max) : `${max}%`

  const getColorClass = () => {
    if (percentage >= dangerThreshold) return 'danger'
    if (percentage >= warningThreshold) return 'warning'
    return 'success'
  }

  return (
    <div className="gauge">
      <div className="gauge-header">
        <span className="gauge-label">{label}</span>
        <span className="gauge-value">
          {displayValue} / {displayMax}
        </span>
      </div>
      <div className="gauge-track">
        <div
          className={`gauge-fill ${getColorClass()}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  )
}

interface ComplianceStatusProps {
  label: string
  status: boolean
  goodWhenTrue?: boolean
}

function ComplianceStatus({ label, status, goodWhenTrue = true }: ComplianceStatusProps) {
  const isGood = goodWhenTrue ? status : !status

  return (
    <div className="compliance-item">
      <span className="compliance-label">{label}</span>
      {isGood ? (
        <CheckCircle size={18} className="compliance-icon success" />
      ) : (
        <XCircle size={18} className="compliance-icon error" />
      )}
    </div>
  )
}

interface RiskMonitorProps {
  ws: WebSocketHookReturn
}

export function RiskMonitor({ ws }: RiskMonitorProps) {
  const queryClient = useQueryClient()

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['risk-metrics'],
    queryFn: riskApi.getMetrics,
    refetchInterval: 5000,
  })

  const { data: circuitBreaker } = useQuery({
    queryKey: ['circuit-breaker'],
    queryFn: riskApi.getCircuitBreaker,
    refetchInterval: 5000,
  })

  const { data: alerts } = useQuery({
    queryKey: ['risk-alerts'],
    queryFn: riskApi.getAlerts,
    refetchInterval: 10000,
  })

  const triggerBreakerMutation = useMutation({
    mutationFn: (reason: string) => riskApi.triggerCircuitBreaker(reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['circuit-breaker'] })
    },
  })

  const resetBreakerMutation = useMutation({
    mutationFn: riskApi.resetCircuitBreaker,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['circuit-breaker'] })
    },
  })

  // Listen for risk alerts via WebSocket
  const handleRiskAlert = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['risk-alerts'] })
    queryClient.invalidateQueries({ queryKey: ['risk-metrics'] })
  }, [queryClient])

  useWebSocketEvent(ws, 'risk_alert', handleRiskAlert)

  const isCircuitBreakerActive = circuitBreaker?.status === 'triggered'
  const activeAlerts = alerts?.filter((a) => a.status === 'active') ?? []

  return (
    <div className="card risk-monitor-card">
      <div className="card-header">
        <h3 className="card-title">
          <Shield size={20} className="icon" />
          Risk Monitor
        </h3>
        {isCircuitBreakerActive && (
          <span className="card-badge error circuit-breaker-badge">
            <Zap size={12} />
            CIRCUIT BREAKER
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* Risk Gauges */}
          <div className="risk-gauges">
            <Gauge
              label="Daily Drawdown"
              value={metrics?.daily_drawdown ?? 0}
              max={metrics?.daily_drawdown_limit ?? 5}
            />
            <Gauge
              label="Total Drawdown"
              value={metrics?.total_drawdown ?? 0}
              max={metrics?.total_drawdown_limit ?? 15}
              warningThreshold={50}
              dangerThreshold={75}
            />
            <Gauge
              label="Portfolio Exposure"
              value={metrics?.portfolio_exposure ?? 0}
              max={metrics?.max_exposure ?? 100}
              warningThreshold={70}
              dangerThreshold={90}
            />
          </div>

          {/* Compliance Status */}
          <div className="compliance-section">
            <h4 className="section-title eyebrow">Compliance</h4>
            <div className="compliance-list">
              <ComplianceStatus
                label="Daily loss within limits"
                status={(metrics?.daily_drawdown ?? 0) < (metrics?.daily_drawdown_limit ?? 5)}
              />
              <ComplianceStatus label="Circuit breakers inactive" status={!isCircuitBreakerActive} />
              <ComplianceStatus
                label="VaR within tolerance"
                status={(metrics?.var_1d ?? 0) < 5}
              />
            </div>
          </div>

          {/* Circuit Breaker Controls */}
          <div className="breaker-section">
            <h4 className="section-title eyebrow">Circuit Breaker</h4>
            <div className="breaker-controls">
              {isCircuitBreakerActive ? (
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => resetBreakerMutation.mutate()}
                  disabled={resetBreakerMutation.isPending}
                >
                  Reset Circuit Breaker
                </button>
              ) : (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => triggerBreakerMutation.mutate('Manual trigger')}
                  disabled={triggerBreakerMutation.isPending}
                >
                  <Zap size={14} />
                  Emergency Stop
                </button>
              )}
              {circuitBreaker?.cooldown_remaining && circuitBreaker.cooldown_remaining > 0 && (
                <span className="cooldown-text">
                  Cooldown: {Math.ceil(circuitBreaker.cooldown_remaining / 60)}m remaining
                </span>
              )}
            </div>
          </div>

          {/* Active Alerts */}
          {activeAlerts.length > 0 && (
            <div className="alerts-section">
              <h4 className="section-title eyebrow">
                <AlertTriangle size={14} />
                Active Alerts
              </h4>
              <div className="alerts-list">
                {activeAlerts.map((alert) => (
                  <div key={alert.id} className={`alert-item ${alert.severity}`}>
                    <div className="alert-message">{alert.message}</div>
                    <div className="alert-time">
                      {new Date(alert.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
